import { useCallback } from 'react';

const MAX_HISTORY = 40;

/**
 * Persist a list of previously entered strings for a given key (localStorage).
 * @param {string} storageKey  localStorage key to use
 */
export function useInputHistory(storageKey: string) {
  const read = (): string[] => {
    try { return JSON.parse(localStorage.getItem(storageKey)) || []; }
    catch { return []; }
  };

  /** Save a new value to the top of history (deduplicates, trims to MAX_HISTORY). */
  const add = useCallback((value: string) => {
    if (!value || !value.trim()) return;
    const trimmed = value.trim();
    const current = read();
    const updated = [trimmed, ...current.filter(v => v !== trimmed)].slice(0, MAX_HISTORY);
    localStorage.setItem(storageKey, JSON.stringify(updated));
  }, [storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Return history entries that contain `query` (case-insensitive),
   * excluding an exact match. Pass empty string to get the full history.
   */
  const getSuggestions = useCallback((query: string) => {
    const all = read();
    if (!query || !query.trim()) return all;
    const q = query.toLowerCase();
    return all.filter(v => v.toLowerCase().includes(q) && v.toLowerCase() !== q);
  }, [storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Remove a specific value from history. */
  const remove = useCallback((value: string) => {
    const current = read();
    localStorage.setItem(storageKey, JSON.stringify(current.filter(v => v !== value)));
  }, [storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return { add, getSuggestions, remove };
}
